import { useState } from 'react'
import type { FacilitatorLiveSessionCard } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { useSendTeamToActivity } from '../../../../apiLayer/queryHooks/mutations'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { TranslateFunction } from '../../../shared/helpers/liveActivityHelpers'
import type { ControlTowerActionOutcome } from './controlTowerHelpers'

const describeSent = (card: FacilitatorLiveSessionCard, t: TranslateFunction): ControlTowerActionOutcome => ({
  severity: 'success',
  message: t('facilitator.controlTower.sendTeamDone', 'Sent {{team}} to {{activity}}.', {
    team: card.teamName,
    activity: card.activityTitle
  })
})

const describeFailure = (t: TranslateFunction): ControlTowerActionOutcome => ({
  severity: 'error',
  message: t('facilitator.controlTower.sendTeamFailed', 'Could not send the team.')
})

/** Asks first, then sends every member of a card's team to its game or debrief page; the outcome stays on the card. */
export const useSendTeamToActivityPage = () => {
  const { t } = useTranslation()
  const sendTeam = useSendTeamToActivity()
  const [cardToSend, setCardToSend] = useState<FacilitatorLiveSessionCard | null>(null)
  const [outcome, setOutcome] = useState<ControlTowerActionOutcome | null>(null)

  const confirm = () => {
    if (!cardToSend) return

    sendTeam.mutate(
      { sprintId: cardToSend.sprintId, activityId: cardToSend.activityId, teamId: cardToSend.teamId },
      {
        onSuccess: (response) => {
          setCardToSend(null)
          setOutcome(response.success ? describeSent(cardToSend, t) : describeFailure(t))
        },
        onError: () => {
          setCardToSend(null)
          setOutcome(describeFailure(t))
        }
      }
    )
  }

  return {
    cardToSend,
    openDialog: setCardToSend,
    cancelDialog: () => setCardToSend(null),
    confirm,
    isSending: sendTeam.isPending,
    outcome,
    clearOutcome: () => setOutcome(null)
  }
}
